import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PlatformLayout } from "../layouts/PlatformLayout";
import { useIdentity } from "../lib/identity";
import { fetchOrgSeats, assignSeat, revokeSeat, transferSeat } from "../lib/sentinel-api";
import type { PricingLicense, CertificationLevel } from "../lib/certifications";
import { levelColor } from "../lib/certifications";

interface SeatRow {
  id: string;
  license_id: string;
  user_id: string;
  status: string;
  assigned_at: string;
  sentinel_users?: { id: string; name: string; email: string; role: string };
  sentinel_licenses?: PricingLicense;
}

function LevelPill({ level }: { level: CertificationLevel }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.125rem 0.5rem",
        borderRadius: "var(--radius-sm)",
        fontSize: "0.6875rem",
        fontWeight: 700,
        background: `${levelColor(level)}15`,
        color: levelColor(level),
      }}
    >
      {level}
    </span>
  );
}

export default function SeatsPage() {
  const { identity } = useIdentity();
  const [activeLicenseId, setActiveLicenseId] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignEmail, setAssignEmail] = useState("");
  const [licenses, setLicenses] = useState<PricingLicense[]>([]);
  const [seats, setSeats] = useState<SeatRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (!identity.organizationId && identity.activeLicenses.length === 0) {
      setLoading(false);
      return;
    }
    const orgId = identity.organizationId ?? "";
    fetchOrgSeats(orgId).then((data) => {
      if (data.licenses?.length) {
        setLicenses(data.licenses);
        setSeats(data.seats ?? []);
      }
      setLoading(false);
    });
  }, [identity.organizationId, identity.activeLicenses]);

  const activeLicense = activeLicenseId ? licenses.find((l) => l.id === activeLicenseId) : null;
  const activeSeats = activeLicenseId ? seats.filter((s) => s.license_id === activeLicenseId && s.status === "active") : [];

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!assignEmail || !activeLicenseId) return;
    setResult("Assigning seat...");
    await assignSeat(activeLicenseId, assignEmail, identity.userId ?? undefined);
    setResult("Seat assigned successfully!");
    setShowAssignModal(false);
    setAssignEmail("");
    setTimeout(() => setResult(null), 3000);
    // Refresh
    const orgId = identity.organizationId ?? "";
    const data = await fetchOrgSeats(orgId);
    if (data.licenses) {
      setLicenses(data.licenses);
      setSeats(data.seats ?? []);
    }
  }

  async function handleRevoke(userId: string) {
    if (!activeLicenseId) return;
    setResult("Revoking seat...");
    await revokeSeat(activeLicenseId, userId);
    setResult("Seat revoked successfully!");
    setTimeout(() => setResult(null), 3000);
    setSeats((prev) => prev.map((s) => (s.user_id === userId && s.license_id === activeLicenseId ? { ...s, status: "revoked" } : s)));
  }

  async function handleTransfer(fromUserId: string) {
    if (!activeLicenseId) return;
    const toEmail = prompt("Enter the email of the user to transfer the seat to:");
    if (!toEmail) return;
    setResult("Transferring seat...");
    await transferSeat(activeLicenseId, fromUserId, toEmail, identity.userId ?? undefined);
    setResult("Seat transferred successfully!");
    setTimeout(() => setResult(null), 3000);
  }

  if (loading) {
    return (
      <PlatformLayout>
        <div style={{ textAlign: "center", padding: "var(--space-8)" }}>
          <p className="text-muted">Loading seat data...</p>
        </div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout>
      <div
        style={{
          background: "linear-gradient(135deg, var(--primary-950), var(--primary-800))",
          borderRadius: "var(--radius-2xl)",
          padding: "var(--space-6) var(--space-5)",
          marginBottom: "var(--space-5)",
          color: "var(--text-on-dark)",
        }}
      >
        <h1 style={{ color: "var(--text-on-dark)", marginBottom: "0.5rem" }}>Seat Manager</h1>
        <p className="lead lead-dark" style={{ fontSize: "1.0625rem", maxWidth: "600px" }}>
          Assign, transfer, and revoke seats across your organization's multi-seat licenses.
        </p>
      </div>

      {result && (
        <div className="card" style={{ padding: "var(--space-3) var(--space-4)", marginBottom: "var(--space-4)", borderColor: "var(--accent-400)", background: "var(--accent-50)", fontSize: "0.875rem", fontWeight: 600, color: "var(--accent-700)" }}>
          {result}
        </div>
      )}

      {licenses.length === 0 ? (
        <div className="card" style={{ padding: "var(--space-5)", textAlign: "center" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "0.5rem" }}>No multi-seat licenses found</h2>
          <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "1rem" }}>
            Purchase an Agency or Enterprise plan to manage seats for your team.
          </p>
          <Link to="/pricing" className="btn btn-primary">View Pricing</Link>
        </div>
      ) : !activeLicense ? (
        <>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Select a License to Manage Seats</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {licenses.map((license) => {
              const assigned = seats.filter((s) => s.license_id === license.id && s.status === "active").length;
              return (
                <div key={license.id} className="card card-hover" style={{ padding: "var(--space-4)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.75rem" }}>
                    <div>
                      <h3 style={{ fontSize: "1rem", marginBottom: "0.25rem" }}>{license.name}</h3>
                      <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>{license.industry}</div>
                      <div style={{ display: "flex", gap: "0.375rem", flexWrap: "wrap" }}>
                        {license.levels_covered?.map((lvl) => (
                          <LevelPill key={lvl} level={lvl} />
                        ))}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: 800 }}>{assigned} / {license.seats}</div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>seats used</div>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.375rem", padding: "0.25rem 0.625rem", borderRadius: "var(--radius-full)", fontSize: "0.6875rem", fontWeight: 600, background: "var(--accent-50)", color: "var(--accent-600)" }}>
                        <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "var(--accent-500)" }} />
                        {license.status}
                      </span>
                    </div>
                  </div>
                  <div style={{ marginTop: "0.75rem" }}>
                    <div style={{ width: "100%", height: "6px", background: "var(--neutral-200)", borderRadius: "var(--radius-full)", overflow: "hidden" }}>
                      <div style={{ width: `${(assigned / license.seats) * 100}%`, height: "100%", background: "linear-gradient(90deg, var(--accent-500), var(--accent-400))", borderRadius: "var(--radius-full)" }} />
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "0.75rem" }}>
                    <button onClick={() => setActiveLicenseId(license.id)} className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>Manage Seats</button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)" }}>
            <div>
              <button onClick={() => setActiveLicenseId(null)} style={{ background: "transparent", border: "none", color: "var(--accent-600)", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", marginBottom: "0.25rem" }}>
                &larr; Back to Licenses
              </button>
              <h2 style={{ fontSize: "1.25rem" }}>{activeLicense.name}</h2>
            </div>
            <button onClick={() => setShowAssignModal(true)} className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>+ Assign Seat</button>
          </div>

          <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: "var(--space-4)" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    {["Name", "Email", "Role", "Status", "Actions"].map((h) => (
                      <th key={h} style={{ textAlign: h === "Actions" ? "right" : "left", padding: "0.75rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeSeats.length === 0 ? (
                    <tr><td colSpan={5} style={{ padding: "1rem", textAlign: "center", color: "var(--text-muted)" }}>No seats assigned yet. Click "Assign Seat" to get started.</td></tr>
                  ) : activeSeats.map((seat) => (
                    <tr key={seat.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem", fontWeight: 600 }}>{seat.sentinel_users?.name ?? "Unknown"}</td>
                      <td style={{ padding: "0.75rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{seat.sentinel_users?.email ?? "N/A"}</td>
                      <td style={{ padding: "0.75rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)", textTransform: "capitalize" }}>{seat.sentinel_users?.role?.replace("_", " ") ?? "student"}</td>
                      <td style={{ padding: "0.75rem 1rem" }}>
                        <span style={{ padding: "0.125rem 0.5rem", borderRadius: "var(--radius-full)", fontSize: "0.6875rem", fontWeight: 600, background: "var(--accent-50)", color: "var(--accent-600)" }}>
                          {seat.status}
                        </span>
                      </td>
                      <td style={{ padding: "0.75rem 1rem", textAlign: "right" }}>
                        <button onClick={() => handleTransfer(seat.user_id)} style={{ background: "transparent", border: "none", color: "var(--primary-600)", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", marginRight: "0.5rem" }}>Transfer</button>
                        <button onClick={() => handleRevoke(seat.user_id)} style={{ background: "transparent", border: "none", color: "var(--error-600)", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>Revoke</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card" style={{ padding: "var(--space-4)" }}>
            <h3 style={{ fontSize: "1rem", marginBottom: "0.75rem" }}>License Details</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.75rem" }}>
              <div>
                <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--neutral-500)", marginBottom: "0.25rem" }}>Total Seats</div>
                <div style={{ fontSize: "0.875rem" }}>{activeLicense.seats}</div>
              </div>
              <div>
                <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--neutral-500)", marginBottom: "0.25rem" }}>Assigned</div>
                <div style={{ fontSize: "0.875rem" }}>{activeSeats.length}</div>
              </div>
              <div>
                <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--neutral-500)", marginBottom: "0.25rem" }}>Available</div>
                <div style={{ fontSize: "0.875rem", color: "var(--accent-600)", fontWeight: 600 }}>{activeLicense.seats - activeSeats.length}</div>
              </div>
            </div>
            <div style={{ marginTop: "0.75rem" }}>
              <Link to="/renewals" className="btn btn-secondary" style={{ fontSize: "0.8125rem" }}>View Renewal Options</Link>
            </div>
          </div>
        </>
      )}

      {showAssignModal && (
        <div
          onClick={() => setShowAssignModal(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "var(--space-4)" }}
        >
          <div onClick={(e) => e.stopPropagation()} className="card" style={{ padding: "var(--space-5)", maxWidth: "440px", width: "100%" }}>
            <h3 style={{ fontSize: "1.125rem", marginBottom: "0.5rem" }}>Assign Seat</h3>
            <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-3)" }}>
              Enter the email of an existing member to assign them a seat on this license.
            </p>
            <form onSubmit={handleAssign}>
              <div className="form-group">
                <label className="form-label" htmlFor="assign-email">Member Email</label>
                <input id="assign-email" type="email" className="form-input" value={assignEmail} onChange={(e) => setAssignEmail(e.target.value)} placeholder="name@organization.gov" />
              </div>
              <div style={{ display: "flex", gap: "0.75rem", marginTop: "var(--space-3)" }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Assign Seat</button>
                <button type="button" onClick={() => setShowAssignModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PlatformLayout>
  );
}
